import React from 'react';
import { useRemoveStaticSeoTags } from '@/components/SEO';

// SEO Keywords organized by category
export const SEO_KEYWORDS = {
  general: [
    'farming news', 'agriculture news', 'farm prices', 'livestock prices',
    'cattle prices', 'mart prices', 'agricultural markets', 'farm market data',
    'farming ireland', 'uk farming', 'usa agriculture', 'global farming news'
  ],
  livestock: [
    'cattle prices ireland', 'livestock mart prices', 'bullock prices',
    'heifer prices', 'weanling prices', 'beef prices', 'dairy cattle',
    'livestock auctions', 'cattle sales', 'mart reports', 'factory prices'
  ],
  markets: [
    'commodity prices', 'grain prices', 'wheat prices', 'barley prices',
    'milk prices', 'beef market', 'pork prices', 'agricultural commodities',
    'farm gate prices', 'futures prices'
  ],
  machinery: [
    'farm machinery', 'tractors for sale', 'agricultural equipment',
    'used farm equipment', 'john deere', 'massey ferguson', 'farm implements'
  ],
  regions: {
    ireland: ['irish farming', 'teagasc', 'irish farmers journal', 'icbf', 'bord bia'],
    uk: ['uk farming', 'nfu', 'defra', 'british agriculture', 'scottish farming'],
    usa: ['usda', 'american agriculture', 'us cattle market', 'corn belt'],
    global: ['world agriculture', 'global food prices', 'international farming']
  }
};

// Default SEO configuration
const DEFAULT_SEO = {
  siteName: 'Farmwatch',
  siteUrl: 'https://www.farmwatch.com',
  defaultTitle: 'Farmwatch - Agricultural Intelligence | Live Farming News & Market Prices',
  defaultDescription: 'Your trusted source for live livestock mart prices, agricultural news, commodity markets, and farming intelligence from Ireland, UK, USA and worldwide.',
  defaultImage: '/og-image.png',
  twitterHandle: '@farmwatch',
  locale: 'en_IE'
};

export default function SEOHead({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  article = null,
  noindex = false,
  structuredData = null
}) {
  useRemoveStaticSeoTags();

  const fullTitle = title 
    ? `${title} | Farmwatch` 
    : DEFAULT_SEO.defaultTitle;
  
  const fullDescription = description || DEFAULT_SEO.defaultDescription;
  const fullImage = image || DEFAULT_SEO.defaultImage;
  const fullUrl = url ? `${DEFAULT_SEO.siteUrl}${url}` : DEFAULT_SEO.siteUrl;
  
  // Combine default keywords with page-specific keywords
  const allKeywords = [
    ...SEO_KEYWORDS.general,
    ...(Array.isArray(keywords) ? keywords : [keywords])
  ].join(', ');

  // Generate JSON-LD structured data
  const generateStructuredData = () => {
    const baseData = {
      '@context': 'https://schema.org',
      '@type': type === 'article' ? 'NewsArticle' : 'WebSite',
      name: DEFAULT_SEO.siteName,
      url: fullUrl,
      description: fullDescription,
      publisher: {
        '@type': 'Organization',
        name: 'Farmwatch',
        logo: {
          '@type': 'ImageObject',
          url: `${DEFAULT_SEO.siteUrl}/logo.png`
        }
      }
    };

    if (type === 'article' && article) {
      return {
        ...baseData,
        '@type': 'NewsArticle',
        headline: title,
        image: fullImage,
        datePublished: article.publishedAt,
        dateModified: article.updatedAt || article.publishedAt,
        author: {
          '@type': 'Organization',
          name: article.source || 'Farmwatch'
        },
        articleSection: article.category || 'Agriculture'
      };
    }

    return structuredData || baseData;
  };

  return (
    <>
      {/* React 19 hoists title/meta/link into <head> automatically */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content={DEFAULT_SEO.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Farmwatch" />
      <meta name="publisher" content="Farmwatch" />
      <meta name="copyright" content="Farmwatch" />
      <meta name="theme-color" content="#0F766E" />
      <meta name="msapplication-TileColor" content="#0F766E" />
      
      {/* Geographic Targeting */}
      <meta name="geo.region" content="IE" />
      <meta name="geo.placename" content="Ireland" />
      <meta name="ICBM" content="53.4129, -8.2439" />
      
      {/* Language */}
      <meta httpEquiv="content-language" content="en" />
      <link rel="alternate" hrefLang="en-ie" href={fullUrl} />
      <link rel="alternate" hrefLang="en-gb" href={fullUrl} />
      <link rel="alternate" hrefLang="en-us" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateStructuredData()) }}
      />
    </>
  );
}

// Pre-configured SEO for specific pages
export const PAGE_SEO = {
  home: {
    title: null, // Uses default
    description: 'Live livestock mart prices, agricultural news, and market intelligence from Ireland, UK, USA and worldwide. Your daily source for farming updates.',
    keywords: [...SEO_KEYWORDS.livestock, ...SEO_KEYWORDS.markets]
  },
  marts: {
    title: 'Livestock Marts & Markets - Live Prices',
    description: 'Live cattle prices from 90+ Irish marts, UK livestock markets, and global agricultural auctions. Weekly price analysis and market reports.',
    keywords: [...SEO_KEYWORDS.livestock, 'mart prices today', 'cattle mart results', 'weekly mart report']
  },
  irishMarts: {
    title: 'Irish Livestock Marts - Live Cattle Prices Ireland',
    description: 'Live prices from 90+ Irish livestock marts. Bullock, heifer, and weanling prices updated daily. Compare marts by county and province.',
    keywords: [...SEO_KEYWORDS.livestock, ...SEO_KEYWORDS.regions.ireland, 'irish cattle prices', 'mart prices ireland']
  },
  ukMarkets: {
    title: 'UK Livestock Markets - British Cattle Prices',
    description: 'Live UK livestock market prices and reports. British cattle, sheep, and pig prices from markets across England, Scotland, and Wales.',
    keywords: [...SEO_KEYWORDS.livestock, ...SEO_KEYWORDS.regions.uk, 'uk cattle prices', 'british livestock']
  },
  usaMarkets: {
    title: 'USA Cattle Markets - American Livestock Prices',
    description: 'US cattle auction prices and market reports. Fed cattle, feeder cattle, and livestock prices from auctions across America.',
    keywords: [...SEO_KEYWORDS.livestock, ...SEO_KEYWORDS.regions.usa, 'us cattle prices', 'american beef market']
  },
  community: {
    title: 'Farming Community - Discussions & Forums',
    description: 'Join the Farmwatch farming community. Discuss agriculture news, share farming tips, and connect with farmers from Ireland, UK, and worldwide.',
    keywords: ['farming forum', 'agriculture community', 'farmer discussions', 'farming advice']
  },
  marketplace: {
    title: 'Farm Marketplace - Buy & Sell Agricultural Equipment',
    description: 'Buy and sell farm machinery, livestock, equipment, and land. Free listings for tractors, implements, and agricultural supplies.',
    keywords: [...SEO_KEYWORDS.machinery, 'farm equipment for sale', 'livestock for sale', 'farm land']
  },
  agriTech: {
    title: 'Agri-Tech - Agricultural Technology & Innovation',
    description: 'Latest agricultural technology news and innovations. Precision farming, smart agriculture, drones, IoT, and sustainable farming tech.',
    keywords: ['agri-tech', 'precision agriculture', 'smart farming', 'farm technology', 'agricultural innovation']
  },
  pricing: {
    title: 'Subscription Plans - Premium Farm Intelligence',
    description: 'Farmwatch subscription plans for premium market data, price alerts, and agricultural intelligence. Free and premium options available.',
    keywords: ['farm subscription', 'market data subscription', 'agricultural intelligence']
  }
};

// Breadcrumb structured data generator
export function generateBreadcrumbData(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://farmwatch.com${item.url}`
    }))
  };
}

// FAQ structured data generator
export function generateFAQData(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

// Product/Listing structured data generator
export function generateListingData(listing) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    description: listing.description,
    image: listing.images?.[0],
    offers: {
      '@type': 'Offer',
      price: listing.price,
      priceCurrency: listing.currency || 'EUR',
      availability: listing.status === 'active' 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Person',
        name: listing.user_email
      }
    },
    category: listing.category
  };
}
